/* eslint-disable react/jsx-pascal-case */
import { useEffect } from "react";
import MarkdownView from "react-showdown";

import * as Api from "@modules/api";
import { createPageElement } from "@modules/content/creators/createPageElement";
import { usePageData } from "@modules/content/hooks/usePageData";
import { ContentPage } from "@modules/content/types";
import PageWithDynamicZoneLayout from "@components/layouts/content/PageWithDynamicZoneLayout";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { OtherAyatListComponent } from "@modules/content/components/OtherAyatListComponent";

import { RamadanBackground } from "./RamadanPage";
import ContentComponentSelector from "@modules/content/components";

export interface LiveTheAyahDetailsParameters {
  text: string | null;
  title: string | null;
  others: string | null;
}

export const LiveTheAyahDetails = createPageElement(
  ({ pageId, context, onPageLoading }, ref) => {
    const navigate = useAppNavigate();

    const { pageData, getParameterValues, refresh, isLoading, content } =
      usePageData<LiveTheAyahDetailsParameters>(
        {
          pageId: pageId ?? "",
          context,
        },
        ref
      );

    const { text, title, others } = getParameterValues([
      "text",
      "title",
      "others",
    ]);

    useEffect(() => {
      if (!pageData?.id || pageData.id !== pageId) {
        onPageLoading && onPageLoading(undefined, isLoading);
      } else {
        onPageLoading && onPageLoading(pageData, isLoading);
      }
    }, [pageData?.id, pageId, isLoading]);

    if (!pageData) return null;

    return (
      <div className="flex h-full flex-col items-start justify-start pt-10">
        <PageWithDynamicZoneLayout
          items={pageData?.dynamicZone ?? []}
          onRefresh={refresh}
          context={content || undefined}
          withContext
        >
          <RamadanBackground />
          <div className="min-h-[700px] px-10">
            <h1 className="text-[44px] font-bold text-ramadan-primary dark:text-dark-ramadan-primary">
              {title}
            </h1>
            <div className="mt-[81px] w-[80%] text-ramadan-primary dark:text-dark-ramadan-primary">
              <MarkdownView
                markdown={text || ""}
                options={{ tables: true, emoji: true }}
                className="flex flex-col items-start justify-start gap-y-3 text-[19px] leading-[35px]"
              />
            </div>
          </div>
          <div className="mt-[35px]">
            <ContentComponentSelector
              identifier="COM:OTHERAYAT:LIST"
              id={others || ""}
              context={context}
              componentRef={null}
            />
          </div>
        </PageWithDynamicZoneLayout>
      </div>
    );
  }
);

export const LiveTheAyahDetailsPage: ContentPage = {
  identifier: "PG:AYAH:DETAILS",
  page: LiveTheAyahDetails,
};
